"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.15,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const galleryImages = [{
  id: 1,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1635001/img/gallery_editorial_wide_1.png",
  alt: "Sparkling Vero Beach kitchen with white marble and coastal sunlight",
  spanClass: "md:col-span-2",
  aspectClass: "aspect-[4/3] md:aspect-[16/9]",
  hasBadge: false,
  link: "/BookYourCleanPage"
}, {
  id: 2,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1635001/img/gallery_texture_close_1.png",
  alt: "Crisp white linens and freshly dusted surfaces in a master suite",
  spanClass: "md:col-span-1",
  aspectClass: "aspect-square md:aspect-[3/4]",
  hasBadge: false,
  link: "/BookYourCleanPage"
}, {
  id: 3,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1635001/img/gallery_portrait_statement_1.png",
  alt: "Polished hardwood floors reflecting the bright Florida coastline",
  spanClass: "md:col-span-1",
  aspectClass: "aspect-[3/4] md:aspect-[4/5]",
  hasBadge: true,
  link: "/BookYourCleanPage"
}, {
  id: 4,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1635001/img/gallery_environmental_urban_1.png",
  alt: "Professional cleaner ensuring every corner of a modern living room shines",
  spanClass: "md:col-span-2",
  aspectClass: "aspect-[16/9] md:aspect-[3/2]",
  hasBadge: false,
  link: "/BookYourCleanPage"
}, {
  id: 5,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1635001/img/gallery_detail_stitching_1.png",
  alt: "Immaculate detail of a sanitized and organized home office workspace",
  spanClass: "md:col-span-3",
  aspectClass: "aspect-[21/9] md:aspect-[2.5/1]",
  hasBadge: false,
  link: "/BookYourCleanPage"
}, {
  id: 6,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1635001/img/gallery_full_body_versatile_1.png",
  alt: "Before and after comparison of a deep-cleaned coastal bathroom",
  spanClass: "md:col-span-2",
  aspectClass: "aspect-[4/3]",
  hasBadge: true,
  link: "/BookYourCleanPage"
}, {
  id: 7,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1635001/img/gallery_texture_close_2.png",
  alt: "Glass doors and mirrors buffed to a streak-free coastal shine",
  spanClass: "md:col-span-1",
  aspectClass: "aspect-square md:aspect-[4/3]",
  hasBadge: false,
  link: "/BookYourCleanPage"
}];
function GalleryItem({
  item,
  index
}: {
  item: typeof galleryImages[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <div data-section-id="225112" ref={ref} data-index={index} className={`relative overflow-hidden group bg-secondary/30 ${item.spanClass} ${item.aspectClass} transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"} rounded-xl`}>
      <img src={item.src} alt={item.alt} className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105" loading={index > 2 ? "lazy" : "eager"} />
      
      {}
      {item.hasBadge && <div className="absolute inset-0 bg-gradient-to-t from-teal-900/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />}

      {item.hasBadge && <Link to={item.link} className="absolute bottom-4 right-4 md:bottom-6 md:right-6 z-10" aria-label={`Book the cleaning service featured in ${item.alt}`}>
          <Badge variant="secondary" className="bg-background/90 backdrop-blur-md text-foreground hover:bg-background shadow-md px-4 py-2 text-xs md:text-sm font-sans tracking-wider uppercase transition-all duration-300 hover:scale-105 rounded-full">
            Book This Clean
          </Badge>
        </Link>}
    </div>;
}
export default function TheSilhouetteGallery() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="sun-kissed-results" className="w-full bg-background py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {}
        <div ref={headerRef} className={`max-w-2xl mb-12 md:mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Sun-Kissed Results
          </h2>
          <p className="text-base md:text-lg text-muted-foreground font-sans max-w-xl leading-relaxed">
            Witness the transformation of Vero Beach homes. From sandy entryways to sparkling kitchens, our detailed cleaning brings the coastal light back into your living space.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 grid-flow-row-dense">
          {galleryImages.map((item, index) => <GalleryItem key={item.id} item={item} index={index} />)}
        </div>
      </div>
    </section>;
}